/**
 * Common unicode characters used throughout the application
 */

// Spaces
const SPACES = Object.freeze({
  /** Non-breaking space - prevents line breaks */
  NON_BREAKING: '\u00A0',
  /** Thin space - smaller than regular space */
  THIN: '\u2009',
  /** Hair space - very thin space */
  HAIR: '\u200A',
  /** Narrow no-breaking space - thin, non-breaking */
  NARROW_NON_BREAKING: '\u202F',
});

// Mathematical symbols
const MATH = Object.freeze({
  /** Degree symbol */
  DEGREE: '\u00B0',
  /** Plus-minus symbol */
  PLUS_MINUS: '\u00B1',
  /** Multiplication sign */
  MULTIPLICATION: '\u00D7',
  /** Multiply dot */
  MULTIPLY_DOT: '\u22C5',
  /** Division sign */
  DIVISION: '\u00F7',
  /** Minus sign */
  MINUS: '\u2212',
  /** Infinity symbol */
  INFINITY: '\u221E',
  /** Square root */
  SQUARE_ROOT: '\u221A',
  /** Approximately equal */
  APPROXIMATELY_EQUAL: '\u2248',
  /** Not equal */
  NOT_EQUAL: '\u2260',
  /** Less than or equal */
  LESS_THAN_EQUAL: '\u2264',
  /** Greater than or equal */
  GREATER_THAN_EQUAL: '\u2265',
});

// Currency symbols
const CURRENCY = Object.freeze({
  /** US Dollar */
  USD: '\u0024',
  /** Euro */
  EUR: '\u20AC',
  /** British Pound */
  GBP: '\u00A3',
  /** Japanese Yen */
  JPY: '\u00A5',
});

const DASH = Object.freeze({
  /** En dash - for ranges */
  EN_DASH: '\u2013',
  /** Em dash - for breaks in thought */
  EM_DASH: '\u2014',
});

const QUOTE = Object.freeze({
  /** Left single quotation mark */
  LEFT_SINGLE_QUOTE: '\u2018',
  /** Right single quotation mark */
  RIGHT_SINGLE_QUOTE: '\u2019',
  /** Left double quotation mark */
  LEFT_DOUBLE_QUOTE: '\u201C',
  /** Right double quotation mark */
  RIGHT_DOUBLE_QUOTE: '\u201D',
});

// Punctuation and dashes
const PUNCTUATION = Object.freeze({
  /** Bullet point */
  BULLET: '\u2022',
  /** Horizontal ellipsis */
  ELLIPSIS: '\u2026',
});

// Special characters
const SPECIAL = Object.freeze({
  /** Copyright symbol */
  COPYRIGHT: '\u00A9',
  /** Registered trademark */
  REGISTERED: '\u00AE',
  /** Trademark */
  TRADEMARK: '\u2122',
  /** Section symbol */
  SECTION: '\u00A7',
  /** Paragraph symbol */
  PARAGRAPH: '\u00B6',
});

// Units and measurements
const UNITS = Object.freeze({
  /** Degree Celsius */
  CELSIUS: '\u2103',
  /** Degree Fahrenheit */
  FAHRENHEIT: '\u2109',
  /** Micro symbol */
  MICRO: '\u00B5',
  /** Ohm symbol */
  OHM: '\u2126',
});

// Type exports for all unicode categories
export type SpaceUnicode = (typeof SPACES)[keyof typeof SPACES];
export type MathSymbolUnicode = (typeof MATH)[keyof typeof MATH];
export type CurrencyUnicode = (typeof CURRENCY)[keyof typeof CURRENCY];
export type DashUnicode = (typeof DASH)[keyof typeof DASH];
export type QuoteUnicode = (typeof QUOTE)[keyof typeof QUOTE];
export type PunctuationUnicode = (typeof PUNCTUATION)[keyof typeof PUNCTUATION];
export type SpecialUnicode = (typeof SPECIAL)[keyof typeof SPECIAL];
export type UnitUnicode = (typeof UNITS)[keyof typeof UNITS];

export const UNICODE = Object.freeze({
  SPACES,
  MATH,
  CURRENCY,
  DASH,
  QUOTE,
  PUNCTUATION,
  SPECIAL,
  UNITS,
});
